import { useContext, useMemo, useState } from "react";
import { useRegisterProcessus, useStableProcessusId } from "features/processus/processusManager";
import { ProcessusContext, ProcessusContextProps } from "./ProcessusProvider";
import { ProcessusDefinition, ProcessusDefinitionNature } from "types/Processus";
import { Pojo } from "types/Galaxy";
import { getDimensionFromEvent } from "utils/component.utils";
import { uuidv4 } from "utils/uuid.utils";
import { useNavigate } from "react-router-dom";
import { FilterBar } from "types/Search";

export function useProcessus(definition: ProcessusDefinition) {
  const context = useContext(ProcessusContext);
  if (context === null) {
    throw new Error("cannot use ProcessLink without ProcessusContext");
  }

  const [managerState, { register: registerProcessus, reset: resetProcessus }] =
    useRegisterProcessus();

  const navigate = useNavigate();

  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);

  const stableProcessusId = useStableProcessusId(definition.id, context.selected ?? [], undefined);

  const machineId = managerState.context.info[stableProcessusId];
  const currentProcessusMachine = managerState.context.processus[machineId ?? ""];

  const hasProcessusStateSpawned =
    currentProcessusMachine !== undefined &&
    !currentProcessusMachine.getSnapshot().matches("error") &&
    !currentProcessusMachine.getSnapshot().matches("done");

  const editionDefault = useMemo(() => {
    let editionDefault: "rapide" | "apercu" | undefined = undefined;
    if (definition.type === "edition") {
      if (definition.apercu) editionDefault = "apercu";
      if (definition.rapide) editionDefault = "rapide";
    }
    return editionDefault;
  }, [
    definition.type === "edition" && definition.apercu,
    definition.type === "edition" && definition.rapide,
    definition.type,
  ]);

  const usableContext = context as ProcessusContextProps;

  const isDisabled =
    !usableContext.selectAll &&
    definition.needEntity &&
    (!usableContext.selected || usableContext.selected.length === 0);

  function launch(editionType?: "rapide" | "apercu", forcedSelected?: Pojo[]) {
    const selected = forcedSelected || usableContext.selected;
    if (isDisabled && (!forcedSelected || forcedSelected.length === 0)) return;

    if (currentProcessusMachine !== undefined) {
      const snapshot = currentProcessusMachine.getSnapshot();

      if (snapshot.matches("error")) {
        resetProcessus(definition.id, usableContext.selected);
      }
    }

    if (
      (definition.type === "traitement" && definition.isAdvanced) ||
      (definition.type === "edition" && definition.isAdvanced)
    ) {
      openAdvanced(
        navigate,
        usableContext.sjmoCode,
        definition.id,
        "",
        definition.type,
        selected,
        null,
        definition.needEntity,
        definition.isOneByOne,
        undefined,
        editionType,
        usableContext.dtFilter,
        usableContext.dtFilterBar,
        usableContext.dtTableName,
        usableContext.selectAll,
        usableContext.selectAllType,
        usableContext.onAfterSaveProcess,
        usableContext.ctrlKey,
      );
      return;
    }
    if (definition.isOneByOne && definition.needEntity) {
      const selectedItems = selected ? selected : [];
      for (let index = 0; index < selectedItems.length; index++) {
        const element = selectedItems[index];
        registerProcessus(
          {
            module: usableContext.sjmoCode,
            compositeID: definition.id,
            type: definition.type,
            selected: [element],
            executeAt: undefined,
            editionType: editionType ?? editionDefault,
            label: definition.label,
            ctrlKey: usableContext.ctrlKey,
          },
          usableContext.onAfterSaveProcess,
        );
      }
    } else {
      registerProcessus(
        {
          module: usableContext.sjmoCode,
          compositeID: definition.id,
          type: definition.type,
          selected: definition.needEntity && selected ? selected : [],
          executeAt: undefined,
          editionType: editionType ?? editionDefault,
          label: definition.label,
          dtFilter: usableContext.dtFilter,
          dtFilterBar: usableContext.dtFilterBar,
          dtTableName: usableContext.dtTableName,
          selectAll: usableContext.selectAll,
          selectAllType: usableContext.selectAllType,
          ctrlKey: usableContext.ctrlKey,
        },
        usableContext.onAfterSaveProcess,
      );
    }
  }

  function onClick(e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) {
    // sur un button, ça peut pas arriver, seulement sur le ProcessusLink déprécié
    const editionType = (e.target as HTMLElement)?.dataset.editionType as
      | "rapide"
      | "apercu"
      | undefined;

    if (usableContext.isDirty() && definition.type !== "navigation") {
      setShowConfirmModal(true);
      const position = getDimensionFromEvent(e as any);
      if (position.width === 0 && position.height === 0) {
        position.width = 200;
        position.height = 50;
        position.x = e.clientX - 100;
        position.y = e.clientY - 100;
      }
      setPosition(position);
    } else {
      launch(editionType);
    }
  }

  return {
    modal: {
      calendar: {
        show: showCalendarModal,
        set: setShowCalendarModal,
      },
      confirm: {
        show: showConfirmModal,
        set: setShowConfirmModal,
      },
      position,
    },
    button: {
      isDisabled,
      onClick,
    },
    editionDefault,
    stableProcessusId: stableProcessusId,
    hasProcessusStateSpawned,
    context: usableContext,
    launch,
  };
}

export function openAdvanced(
  navigate: ReturnType<typeof useNavigate>,
  module: string,
  processID: string,
  navigationUrl: string,
  type: ProcessusDefinitionNature,
  selected: Pojo[] | undefined,
  delay: string | null,
  needEntity: boolean,
  isOneByOne: boolean,
  label?: string,
  editionType?: string,
  dtFilter?: string,
  dtFilterBar?: FilterBar,
  dtTableName?: string,
  selectAll?: boolean,
  selectAllType?: "DATATABLE" | "INTERACTIVE_REPORT",
  refresh?: () => void,
  ctrlKey?: string,
) {
  const params = new URLSearchParams();
  params.set("sjmoCode", module);
  params.set("process", processID);
  params.set("navigationUrl", navigationUrl);
  params.set("type", type);
  if (delay) {
    params.set("delay", delay);
    params.set("forAll", "true");
  }
  if (label) {
    params.set("processLabel", label);
  }
  if (editionType) {
    params.set("editionType", editionType);
  }
  if (ctrlKey) {
    params.set("ctrlKey", ctrlKey);
  }

  const uuid = uuidv4();
  (window as any)[uuid] = () => refresh?.();

  navigate(
    {
      search: params.toString(),
    },
    {
      state: {
        selected,
        refresh: uuid,
        needEntity,
        isOneByOne,
        dtFilter,
        dtFilterBar,
        dtTableName,
        selectAll,
        selectAllType,
      },
    },
  );
}
